/**
 * A widget that can display some text, with a button that when clicked copies
 * the text to the user's clipboard.
 */
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useCallback, useRef, useState} from "react";

import Button, {type ButtonProps} from "@khanacademy/wonder-blocks-button";
import Clickable from "@khanacademy/wonder-blocks-clickable";
import {Id, View} from "@khanacademy/wonder-blocks-core";
import {TextField} from "@khanacademy/wonder-blocks-form";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {RequestInfo} from "@khan/shared-core";
import {mediaQueries} from "@khan/shared-styles";

// We special case some styles used in the class-creator modal and screens for
// mobile screen sizes.
const mobileViewCutoffQuery = mediaQueries.smOrSmaller;

type CopyStatus = "uncopied" | "success" | "failedOnIOS" | "failed";

interface Props {
    inputId?: string;
    testId?: ButtonProps["testId"];
    startIcon?: ButtonProps["startIcon"];
    value: string;
    buttonKind?: ButtonProps["kind"];
    stackOnMobile?: boolean;
    copyText?: () => void;
}

export const CopyableTextInput = (props: Props) => {
    const {
        inputId,
        testId,
        startIcon,
        value,
        buttonKind = "secondary",
        stackOnMobile = true,
        copyText: propCopyText,
    } = props;

    const [copyStatus, setCopyStatus] = useState<CopyStatus>("uncopied");
    const copyTextIDRef = useRef<string>("");

    // In order to maximize cross-browser clipboard support for iOS 10+ and
    // other platforms, we use the following method to select a range from an
    // input and execute the copy command.
    // See: https://stackoverflow.com/a/34046084
    const copyText = useCallback(() => {
        propCopyText?.();
        const inputElem = document.getElementById(copyTextIDRef.current);
        if (inputElem instanceof HTMLInputElement) {
            // Focus on the text input that we want to copy from. This prevents
            // the keyboard from showing up on iOS -- we want to copy, so the
            // keyboard is not necessary.
            inputElem.focus();

            const {contentEditable, readOnly} = inputElem;
            const range = document.createRange();

            // In iOS, a copyable input must not be read-only or have
            // contentEditable set to false.
            inputElem.contentEditable = "true";
            inputElem.readOnly = false;
            range.selectNodeContents(inputElem);

            const selection = window.getSelection();
            // @ts-expect-error - TS2531 - Object is possibly 'null'.
            selection.removeAllRanges();
            // @ts-expect-error - TS2531 - Object is possibly 'null'.
            selection.addRange(range);

            inputElem.select();
            inputElem.setSelectionRange(0, inputElem.value.length);

            // Restore previous contentEditable and read-only settings.
            inputElem.contentEditable = contentEditable;

            // Keep the underlying <input/> element enabled
            // in order to maintain text selection.
            // Also have it be read-only so that editing
            // is not possible (no blinking caret).
            inputElem.readOnly = readOnly;

            const copied = document.execCommand("copy");
            setCopyStatus(
                copied
                    ? "success"
                    : RequestInfo.isIOS
                      ? "failedOnIOS"
                      : "failed",
            );
        }
    }, [propCopyText]);

    const renderInput = useCallback(
        (inputId: string) => {
            const copyStatusMessage: {
                uncopied: string;
                success: string;
                failedOnIOS: string;
                failed: string;
            } = {
                uncopied: t`Copy`,
                success: t`Copied`,
                failedOnIOS: t`Tap the black "Copy" button`,
                failed: t`Failed to copy`,
            };

            copyTextIDRef.current = inputId;

            return (
                <View
                    style={[
                        styles.input,
                        stackOnMobile && styles.stackOnMobile,
                    ]}
                >
                    <Clickable
                        style={styles.textFieldClickable}
                        hideDefaultFocusRing={true}
                        onClick={copyText}
                    >
                        {() => (
                            <TextField
                                testId={testId}
                                id={copyTextIDRef.current}
                                value={value}
                                onChange={() => {}}
                                readOnly={true}
                            />
                        )}
                    </Clickable>
                    <Button
                        kind={buttonKind}
                        style={
                            buttonKind === "tertiary"
                                ? styles.tertiaryButton
                                : styles.btnWithBackground
                        }
                        disabled={copyStatus !== "uncopied"}
                        onClick={copyText}
                        startIcon={startIcon || undefined}
                    >
                        {copyStatusMessage[copyStatus]}
                    </Button>
                </View>
            );
        },
        [
            testId,
            value,
            buttonKind,
            stackOnMobile,
            copyText,
            copyStatus,
            startIcon,
        ],
    );

    return <Id id={inputId}>{(id) => renderInput(id)}</Id>;
};

const styles = StyleSheet.create({
    input: {
        display: "flex",
        flexDirection: "row",
    },
    stackOnMobile: {
        [mobileViewCutoffQuery]: {
            flexDirection: "column",
        },
    },
    btnWithBackground: {
        marginInlineStart: spacing.xSmall_8,
        flexGrow: 1,

        [mobileViewCutoffQuery]: {
            marginTop: spacing.xSmall_8,
            marginLeft: 0,
        },
    },
    tertiaryButton: {
        marginInlineStart: spacing.xSmall_8,
    },
    textFieldClickable: {
        width: "100%",
    },
});
